import { Component, ElementRef, Host, OnDestroy, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, ViewChild, AfterViewInit, Injector } from '@angular/core';
import { scrollIntoViewAsync, DestroyGuard } from 'services/browser-utils';
import { KeyCode, KeyboardOptions, KeyboardOptionsProvider, VirtualKeyboardService, BorrowedElementAction } from 'services/virtual-keyboard.service';
import { MeasureUnitConfig } from '@cf-shared/measure-configs';
import { SettingsService } from 'services/settings.service';
import { CsRollerListInterface } from 'components/cs-roller/cs-roller-list-interface';
import { findNextVisibleSequenceElement } from 'util/input-sequence';
import { CfPopoverBase } from 'components/popover-host/popover-interface';
import { InputValue } from './input-value';
import { KeyboardScrollSync, findScrollContainer } from './keyboard-scroll-sync';
import { PopupMenuProvider } from 'providers/popup-menu/popup-menu';
import { sleep } from '@cf-shared/shared-util';
import { shortUnitName, translateMeasureDescription } from 'util/formatting';
import { untranslatedString } from 'models/translated-string';
import { triggerFeedback, Feedback } from 'services/haptics.service';
import { BorrowedElementHiding, hideBorrowedElement, restoreBorrowedElement } from './borrowed-element-hiding';

import { LL } from 'i18n/i18n.service';
// Interface for next element in sequence
interface NextInSequence {
  element?: HTMLElement;           // The next DOM element in the input sequence
  hiding?: BorrowedElementHiding;  // How the element was hidden (to restore it); unset once restored
  action?: BorrowedElementAction;  // Action to take when confirming (Click or Focus)
  label?: string;                  // Button label text to show on keyboard confirm button
  isDisabled?: boolean;            // Whether the next element is disabled (mirrored on confirm button)
}

// Re-export InputValue for backward compatibility
export { InputValue } from './input-value';

// Enums for stronger typing
enum KeyboardView {
  Keyboard = 'keyboard',
  Roller = 'roller'
}

enum UnitDisplayMode {
  None = 'none',        // No units to display
  Toggle = 'toggle',    // 2 units - show both as toggles
  Selector = 'selector' // 3+ units - show current with selector button
}

// How long the roller must rest before its value is committed to the parent.
// While the wheel is moving we only do cheap per-tick work (haptic tick +
// display update); the full commit pipeline (parent callback, plate-loading
// solver, change detection) runs once after the wheel settles. Committing on
// every tick froze the wheel at high weights on slow devices (#4042).
const ROLLER_COMMIT_SETTLE_MS = 200;

// Tells a cs-roller component what text to show on each row of one column.
class RollerListProvider extends CsRollerListInterface {
  constructor(
    private values: InputValue[]
  ) {
    super();
  }

  getTotalElements(): number {
    return this.values.length;
  }

  getElement(index: number): string {
    return this.values[index]?.label || '';
  }
}

// One column of the roller picker. The keyboard shows either one of these (a
// single wheel, e.g. a weight) or two side by side (a dual wheel, e.g. feet and
// inches). Everything a column needs to run lives here, so the two columns are
// one piece of code driven twice instead of two numbered copies that drift.
export class RollerLane {
  // every value this column can be spun to
  values: InputValue[] = [];
  // the value the wheel currently rests on
  selected: InputValue | null = null;
  // where the wheel starts when the input is empty
  suggested: InputValue | null = null;
  // the unit shown above the column, e.g. "KG"
  label: string = undefined;
  // what the cs-roller component reads its row text from; null while unused
  provider: RollerListProvider | null = null;
  // which row of the column the wheel rests on
  currentIndex: number = 0;
  // the last row the tick handler saw. -1 means "no tick seen yet", which is
  // how the handler knows to stay silent when the wheel is first positioned
  // rather than spun by the user.
  lastIndex: number = -1;

  // forget the values and the unit, but keep handing the wheel its rows. This
  // is what closing the keyboard does, and it deliberately leaves currentIndex
  // and provider alone, exactly as it did when these were numbered fields on
  // the component: only initializeRollerView moves the index, and the wheel may
  // still be on screen while the keyboard slides shut.
  clearValuesAndLabel(): void {
    this.values = [];
    this.selected = null;
    this.suggested = null;
    this.label = undefined;
  }

  // the same, and stop feeding the wheel as well. This is what a column that is
  // not used by the input being opened gets, so nothing is left pointing at the
  // previous input's rows.
  clearAndDropProvider(): void {
    this.clearValuesAndLabel();
    this.provider = null;
  }
}

@Component({
    selector: 'virtual-keyboard',
    templateUrl: 'virtual-keyboard.html',
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class VirtualKeyboardComponent extends CfPopoverBase implements OnInit, OnDestroy, AfterViewInit {
  private destroyGuard = new DestroyGuard();
  protected LL = LL;
  // Make enums available to template
  KeyCode = KeyCode;
  KeyboardView = KeyboardView;
  UnitDisplayMode = UnitDisplayMode;

  keyboardShown = false;
  resizeObserver: ResizeObserver = undefined;
  targetElement: HTMLElement = undefined;
  private optsProvider: KeyboardOptionsProvider;
  opts: KeyboardOptions = {};
  initialOpts: KeyboardOptions = {}; // Initial options when created as popover
  
  // Next element in sequence tracking
  private nextInSequence: NextInSequence = {};
  
  // New properties for Figma design
  private _currentUnit: string = '';
  private _currentView: KeyboardView = KeyboardView.Keyboard;

  // use setters to handle side effects when cs-segment changes selection
  get currentUnit(): string {
    return this._currentUnit;
  }
  set currentUnit(value: string) {
    if (this._currentUnit !== value) {
      const oldValue = this._currentUnit;
      this._currentUnit = value;
      // only trigger side effects if keyboard is shown (not during initialization)
      if (this.keyboardShown && oldValue) {
        void this.handleUnitChange(value);
      }
    }
  }

  get currentView(): KeyboardView {
    return this._currentView;
  }
  set currentView(value: KeyboardView) {
    if (this._currentView !== value) {
      const oldValue = this._currentView;
      this._currentView = value;
      // only trigger side effects if keyboard is shown (not during initialization)
      if (this.keyboardShown && oldValue) {
        void this.handleViewChange(value);
      }
    }
  }
  displayValue: string = '';
  isValueSelected: boolean = false; // Track if value is selected for replacement
  unitConfigs: MeasureUnitConfig[] = [];
  unitDisplayMode: UnitDisplayMode = UnitDisplayMode.None;
  unit1: { id: string; label: string } = { id: '', label: '' };
  unit2: { id: string; label: string } | null = null;
  alwaysShowUnit: boolean = false;
  
  // Roller properties
  @ViewChild('valueDisplay') valueDisplayRef: ElementRef;
  
  // The roller picker's columns. lane1 runs the single wheel and the left-hand
  // wheel of a dual; lane2 runs the right-hand wheel and carries nothing at all
  // unless isDualRoller is true.
  isDualRoller: boolean = false;
  @ViewChild('rollerList1') rollerList1Ref: ElementRef;
  @ViewChild('rollerList2') rollerList2Ref: ElementRef;
  lane1 = new RollerLane();
  lane2 = new RollerLane();

  // Force recreation of roller components when switching inputs
  private rollerKey = 0;
  roller1Instances = [{ key: 0 }];
  roller2Instances = [{ key: 0 }];

  // Navigation hint for paired inputs
  private shouldSkipNextInSequence = false;

  // Track when keyboard is showing its own popover to prevent dismissal
  public isShowingInternalPopover = false;

  // TrackBy function for ngFor
  trackRollerInstance = (_index: number, item: { key: number }) => item.key;
  
  // Virtual scrolling properties
  
  
  
  // Getter for current unit label for display
  get currentUnitLabel(): string {
    const config = this.unitConfigs.find(c => c.id === this.currentUnit);
    return config ? this.getUnitLabel(config) : this.currentUnit.toUpperCase();
  }
  
  // Computed properties for template compatibility
  get showUnitToggle(): boolean {
    return this.unitDisplayMode !== UnitDisplayMode.None;
  }
  
  get showUnitSelector(): boolean {
    return this.unitDisplayMode === UnitDisplayMode.Selector;
  }
  
  // Get the label for the confirm button
  get confirmButtonLabel(): string {
    // If we have a label from next element, use it directly (already translated)
    if (this.nextInSequence.label) {
      return this.nextInSequence.label;
    }
    // Return empty string to trigger fallback to translated default
    return '';
  }
  
  // Check if confirm button should be disabled (mirroring next element)
  get isConfirmButtonDisabled(): boolean {
    return this.nextInSequence.isDisabled || false;
  }
  
  // Keeps the page behind the keyboard scrolled and padded in step with it
  private scrollSync: KeyboardScrollSync;

  // Asked every frame by the scroll synchroniser, which keeps running through
  // the closing animation until the keyboard is both dismissed and off screen
  private isKeyboardShown = () => this.keyboardShown;

  constructor(
    @Host() private host: ElementRef,
    public keyboardService: VirtualKeyboardService,
    private cdr: ChangeDetectorRef,
    private settings: SettingsService,
    private injector: Injector,
    private popupMenu: PopupMenuProvider
  ) {
    super();
    this.scrollSync = new KeyboardScrollSync(this.host.nativeElement, this.keyboardService);
  }

  
  private getFreshOptions(): KeyboardOptions {
    return this.optsProvider ? this.optsProvider() : {};
  }
  
  private determineStartingView(opts: KeyboardOptions): KeyboardView {
    // If we have roller values available
    const hasRollerValues = (opts.preselectValuesNumeric1 && opts.preselectValuesNumeric1.length > 0) || 
                           (opts.preselectValuesNumeric2 && opts.preselectValuesNumeric2.length > 0);
    
    if (!hasRollerValues) {
      // No roller values available, must use keyboard
      return KeyboardView.Keyboard;
    }

    // This control offers only the roller, and its view toggle is hidden. So
    // the roller wins over the saved preference below: were it the other way
    // round, a user whose remembered view is the keypad would open onto a
    // keypad with no toggle to get back to the roller (#4904).
    if (opts.rollerOnly) {
      return KeyboardView.Roller;
    }

    // If user has a saved preference, use it
    if (this.settings.keyboardLastView) {
      return this.settings.keyboardLastView === 'roller' ? KeyboardView.Roller : KeyboardView.Keyboard;
    }
    
    // Otherwise, use the option provided (which defaults to roller if startInRollerMode is true)
    return opts.startInRollerMode ? KeyboardView.Roller : KeyboardView.Keyboard;
  }

  
  
  
  // Complete shutdown of keyboard - as if closing it completely
  private shutdownKeyboard() {
    // Skip if already shut down
    if (!this.targetElement && !this.displayValue) {
      return;
    }

    // If in roller mode, apply the selected values before shutting down
    if (this.currentView === KeyboardView.Roller && this.opts?.onValueSelectedNumeric) {
      // Note: We don't await here since we're shutting down anyway
      void this.returnRollerValues();
    }
    // In keyboard mode, the value is already in displayValue and will be handled by parent

    // Restore visibility of next element if it was hidden and not already restored
    this.restoreBorrowedElementIfHidden();

    // Call onClose callback if provided (only once)
    if (this.opts?.onClose) {
      this.opts.onClose();
    }

    // Clear all state including targetElement reference
    this.targetElement = undefined;
    this.displayValue = '';
    this.isValueSelected = false;
    this.lane1.clearValuesAndLabel();
    this.lane2.clearValuesAndLabel();
    this.isDualRoller = false;

    // Clear next element properties
    this.nextInSequence = {};
  }
  
  // Force recreation of roller components
  private resetRollers(): void {
    this.rollerKey++;
    this.roller1Instances = [{ key: this.rollerKey }];
    this.roller2Instances = [{ key: this.rollerKey + 1000 }]; // Different key for second roller
    // This will force Angular to destroy and recreate the cs-roller components
  }
  
  // Restore the borrowed element's inline style if the keyboard hid it and has not restored it yet
  private restoreBorrowedElementIfHidden(): void {
    const { element, hiding } = this.nextInSequence;
    if (!element || !hiding) return;
    restoreBorrowedElement(element, hiding);
    this.nextInSequence.hiding = undefined;
  }

  // Update next element based on current sequence state
  private updateNextInSequence(): void {
    // First restore any previously borrowed element
    this.restoreBorrowedElementIfHidden();
    
    // Clear previous state
    this.nextInSequence = {};

    // Find the next element in the input sequence if the parent has input-sequence attribute
    if (!this.targetElement) return;
    
    // Find the parent element with input-sequence attribute
    const sequenceElement = this.targetElement.closest('[input-sequence]') as HTMLElement;
    if (!sequenceElement) return;
    
    let nextElement = findNextVisibleSequenceElement(sequenceElement);
    if (!nextElement) return;

    // Check if we should skip the next element (for paired inputs in roller mode)
    if (this.shouldSkipNextInSequence && this.currentView === KeyboardView.Roller && nextElement) {
      // Skip to the element after the paired one
      const elementAfterPaired = findNextVisibleSequenceElement(nextElement);
      if (elementAfterPaired) {
        nextElement = elementAfterPaired;
      } else {
        // No element after paired, so no next element
        return;
      }
    }
    
    // Extract label from the next element
    if (nextElement.tagName === 'CS-BUTTON') {
      // Check if button is disabled
      const isDisabled = nextElement.hasAttribute('disabled') || 
                        nextElement.getAttribute('aria-disabled') === 'true' ||
                        nextElement.classList.contains('disabled');
      
      // Mirror the disabled state
      this.nextInSequence.isDisabled = isDisabled;
      
      // Extract its label (even if disabled)
      const labelAttr = nextElement.getAttribute('label');
      if (labelAttr) {
        // Label attribute already contains translated text from [label]="'key' | translate"
        this.nextInSequence.label = labelAttr;
      } else {
        // Try to get text content
        this.nextInSequence.label = nextElement.textContent?.trim() || 'Next';
      }
      this.nextInSequence.element = nextElement;
      this.nextInSequence.action = BorrowedElementAction.Click;
      // Hide the button, remembering how, so it can be restored (a button marked
      // keep-space-when-borrowed keeps its space, see borrowed-element-hiding.ts)
      this.nextInSequence.hiding = hideBorrowedElement(nextElement);
    } else if (nextElement.tagName === 'CS-INPUT-MEASURE') {
      // Another cs-input-measure - focus it
      this.nextInSequence.label = 'Next';
      this.nextInSequence.element = nextElement;
      this.nextInSequence.action = BorrowedElementAction.Focus;
    } else if (nextElement.tagName === 'INPUT' || nextElement.tagName === 'CS-INPUT' || nextElement.tagName === 'TEXTAREA') {
      // Regular input field - focus it
      this.nextInSequence.label = 'Next';
      this.nextInSequence.element = nextElement;
      this.nextInSequence.action = BorrowedElementAction.Focus;
    }
  }
  
  // Complete initialization of keyboard - as if opening it fresh
  private async initializeKeyboard() {
    // A commit scheduled for the previous target must not fire AFTER the
    // keyboard re-targets (input switch / view switch / unit switch) - the
    // roller state is about to be rebuilt for the new input. But it must
    // not be silently dropped either: on a fast input switch (within the
    // settle window) the paired dual-roller component would then never
    // receive its editDone (the old target's onClose only covers the
    // component that opened the keyboard). So flush the pending commit NOW,
    // synchronously, while this.opts still belongs to the previous target.
    // returnRollerValues cancels the pending timer as its first step.
    if (this.pendingRollerCommitTimer !== null) {
      void this.returnRollerValues();
    }

    // Reset the last-seen roller indices so the "skip the initial tick"
    // guard in the index handlers works on every (re)open, not just the
    // first one - otherwise reopening the keyboard on a different value
    // plays a spurious haptic tick
    this.lane1.lastIndex = -1;
    this.lane2.lastIndex = -1;

    // Force recreation of rollers when switching inputs
    this.resetRollers();
    
    // Get fresh options first
    const opts = this.getFreshOptions();
    this.opts = opts;

    // Store navigation hint for paired inputs
    this.shouldSkipNextInSequence = opts.shouldSkipNextInSequence || false;

    // Update targetElement from options if provided (for unit switching case)
    if (opts.targetElement) {
      this.targetElement = opts.targetElement;
    }
    
    // Validate targetElement is still in DOM (e.g., when switching from dual-unit to single-unit)
    if (this.targetElement && !this.targetElement.isConnected) {
      // Try to find the primary input if secondary was removed
      const inputMeasure = document.querySelector('cs-input-measure');
      if (inputMeasure) {
        const primaryElement = inputMeasure.querySelector('input:not(.secondary), .display-value:not(.secondary)') as HTMLElement;
        if (primaryElement) {
          this.targetElement = primaryElement;
        }
      }
    }
    
    // Initialize display value from target element
    // For input elements (desktop), use .value; for div elements (mobile), use opts.initialDisplayText
    const inputValue = this.targetElement instanceof HTMLInputElement
      ? this.targetElement.value
      : (opts.initialDisplayText || '');
    if (inputValue) {
      this.displayValue = inputValue;
      // Set initial selection state for non-empty values in keypad mode
      this.isValueSelected = this.currentView === KeyboardView.Keyboard && this.displayValue.length > 0;
      // TODO: FAKE_SELECTION - reimplement for cursor/selection visuals on mobile
      if (this.isValueSelected && this.targetElement instanceof HTMLInputElement) {
        this.selectInputText();
      }
    } else {
      this.displayValue = '';
      this.isValueSelected = false;
    }

    // Update next element based on current sequence
    this.updateNextInSequence();
    
    // Set up unit configuration
    // Always use units from options - they come from the appropriate input
    this.unitConfigs = opts.unitConfigs || [];

    if (this.unitConfigs.length === 0) {
      this.unitDisplayMode = UnitDisplayMode.None;
    } else {
      this._currentUnit = opts.currentUnitId || this.unitConfigs[0]?.id || '';
      await this.setupUnitToggle();
      this.updateUnitDisplay();
    }
    
    // Initialize roller configuration based on fresh options
    if (opts.isDualRoller && opts.preselectValuesNumeric1 && opts.preselectValuesNumeric2) {
      // Dual roller mode - both columns carry values
      this.isDualRoller = true;
      this.configureLane(this.lane1, opts.preselectValuesNumeric1,
        opts.currentValueNumeric1, opts.suggestedValueNumeric1, opts.rollerLabel1);
      this.configureLane(this.lane2, opts.preselectValuesNumeric2,
        opts.currentValueNumeric2, opts.suggestedValueNumeric2, opts.rollerLabel2);
    } else if (opts.preselectValuesNumeric1 && opts.preselectValuesNumeric1.length > 0) {
      // Single roller mode - only the first column is used
      this.isDualRoller = false;
      this.configureLane(this.lane1, opts.preselectValuesNumeric1,
        opts.currentValueNumeric1, opts.suggestedValueNumeric1, opts.rollerLabel1);
      this.lane2.clearAndDropProvider();
    } else {
      // No roller values
      this.isDualRoller = false;
      this.lane1.clearAndDropProvider();
      this.lane2.clearAndDropProvider();
    }
    
    // Determine starting view based on saved preference or options
    const startingView = this.determineStartingView(opts);
    this.currentView = startingView;
    
    // Initialize the appropriate view
    if (this.currentView === KeyboardView.Roller) {
      await this.initializeRollerView();
    } else {
      this.initializeKeyboardView();
    }
    
    // Force change detection
    this.cdr.markForCheck();
  }
  
  // Set one roller column up from the options the calling screen passed. Both
  // the single-wheel and the dual-wheel case go through here, so the two
  // columns cannot be set up differently by accident.
  private configureLane(
    lane: RollerLane,
    values: InputValue[],
    currentValue: number | undefined,
    suggestedValue: number | undefined,
    label: string | undefined,
  ): void {
    lane.values = values;

    // if the input already holds a value, start the wheel on it
    if (currentValue !== undefined && isFinite(currentValue)) {
      lane.selected = InputValue.findByValue(currentValue, values) ||
        InputValue.findClosest(currentValue, values);
    } else {
      lane.selected = this.suggestedOrFirstValue(suggestedValue, values);
    }

    lane.suggested = this.suggestedOrFirstValue(suggestedValue, values);
    lane.label = label || undefined;
    lane.provider = new RollerListProvider(values);
  }

  // Where a wheel starts when the input holds no value of its own: the
  // suggestion if the calling screen offered one, otherwise the first value on
  // the wheel.
  private suggestedOrFirstValue(suggestedValue: number | undefined, values: InputValue[]): InputValue | null {
    if (suggestedValue !== undefined) {
      return InputValue.findClosest(suggestedValue, values);
    }
    return values[0] || null;
  }

  // Initialize roller view with proper values
  private async initializeRollerView() {
    if (this.isDualRoller) {
      // Parse current input value to determine roller positions
      // Handle value1 - use current if available, otherwise suggested
      if (this.opts.currentValueNumeric1 !== undefined && isFinite(this.opts.currentValueNumeric1)) {
        this.lane1.selected = InputValue.findByValue(this.opts.currentValueNumeric1, this.lane1.values) ||
          InputValue.findClosest(this.opts.currentValueNumeric1, this.lane1.values);
      } else {
        // Fall back to suggested value for value1
        this.lane1.selected = this.lane1.suggested || this.lane1.values[0] || null;
      }

      // Handle value2 independently - use current if available, otherwise suggested
      if (this.opts.currentValueNumeric2 !== undefined && isFinite(this.opts.currentValueNumeric2)) {
        this.lane2.selected = InputValue.findByValue(this.opts.currentValueNumeric2, this.lane2.values) ||
          InputValue.findClosest(this.opts.currentValueNumeric2, this.lane2.values);
      } else {
        // Fall back to suggested value for value2
        this.lane2.selected = this.lane2.suggested || this.lane2.values[0] || null;
      }

      // Display both values with unit labels
      if (this.lane1.selected && this.lane2.selected) {
        this.updateDisplayValueAndScroll(this.dualDisplayText());
      }

      // Initialize scroll positions using numeric comparison
      const idx1 = this.lane1.selected ?
        InputValue.findIndexByNumeric(this.lane1.selected.numeric, this.lane1.values) : 0;
      const idx2 = this.lane2.selected ?
        InputValue.findIndexByNumeric(this.lane2.selected.numeric, this.lane2.values) : 0;

      // Set the correct indices - rollers will be fresh from resetRollers()
      this.setLaneIndex(this.lane1, Math.max(0, idx1));
      this.setLaneIndex(this.lane2, Math.max(0, idx2));
      
      // Wait for Angular to update the DOM before initializing roller positions
      if (!await this.destroyGuard.safeWaitForAngularUpdate(this.injector)) return;
      // Roller positions are now handled by cs-roller component
    } else {
      // Single roller - determine value from input
      if (this.opts.currentValueNumeric1 !== undefined && isFinite(this.opts.currentValueNumeric1)) {
        // Use numeric value if available
        this.lane1.selected = InputValue.findByValue(this.opts.currentValueNumeric1, this.lane1.values) ||
          InputValue.findClosest(this.opts.currentValueNumeric1, this.lane1.values);
      } else if (this.targetElement instanceof HTMLInputElement && this.targetElement.value) {
        // Try to parse target element value as number (desktop only - input elements)
        const targetValue = parseFloat(this.targetElement.value.replace(/,/, '.'));
        if (isFinite(targetValue)) {
          this.lane1.selected = InputValue.findByValue(targetValue, this.lane1.values) ||
            InputValue.findClosest(targetValue, this.lane1.values);
        } else {
          this.lane1.selected = this.lane1.suggested || this.lane1.values[0] || null;
        }
      } else {
        // Use suggested value
        this.lane1.selected = this.lane1.suggested || this.lane1.values[0] || null;
      }

      // Display the selected value
      if (this.lane1.selected) {
        this.updateDisplayValueAndScroll(this.lane1.selected.label);
      }

      // Initialize scroll position using numeric comparison
      const idx = this.lane1.selected ?
        InputValue.findIndexByNumeric(this.lane1.selected.numeric, this.lane1.values) : 0;

      // Set the correct index - rollers will be fresh from resetRollers()
      this.setLaneIndex(this.lane1, Math.max(0, idx));
      this.setLaneIndex(this.lane2, 0);

      // Wait for Angular to update the DOM before initializing roller position
      if (!await this.destroyGuard.safeWaitForAngularUpdate(this.injector)) return;
      // Roller position is now handled by cs-roller component
    }
  }
  
  // Initialize keyboard view
  private initializeKeyboardView() {
    // Show the current input value as-is
    // Check that targetElement is still connected to DOM
    if (this.targetElement && this.targetElement.isConnected) {
      // For input elements (desktop), use .value; for div elements (mobile), use opts.initialDisplayText
      const displayValue = this.targetElement instanceof HTMLInputElement
        ? this.targetElement.value
        : (this.opts.initialDisplayText || '');
      this.updateDisplayValueAndScroll(displayValue);
    } else {
      // if element was removed (e.g., secondary input after unit change), show empty
      this.updateDisplayValueAndScroll('');
    }
  }

  async showKeyboard(targetElement: HTMLElement, optsProvider: KeyboardOptionsProvider) {
    // if switching to a different target, notify the old input that editing is done
    if (this.targetElement && this.targetElement !== targetElement && this.opts?.onClose) {
      this.opts.onClose();
    }

    // Store the options provider and target element
    this.optsProvider = optsProvider;
    this.targetElement = targetElement;

    // Always show the keyboard UI when called
    this.keyboardShown = true;

    // Always initialize keyboard (handles both new and existing keyboard)
    await this.initializeKeyboard();

    // Start syncing the page immediately, from the first frame of the animation
    // Don't wait for ResizeObserver, as that causes lag
    if (!await this.destroyGuard.safeWaitForAngularUpdate(this.injector)) return;
    this.scrollSync.start(this.targetElement, this.isKeyboardShown);
  }
  
  // Implement CfPopoverInterface
  onWillDismiss(_data: any): void {
    // Apply any pending values and cleanup
    this.shutdownKeyboard();

    // Mark keyboard as hidden
    this.keyboardShown = false;

    // Don't hide the keyboard UI immediately - let the popover animation handle it
    // The popover-host will handle the animation and removal

    // Keep the padding observer running during dismiss animation
    // It will track the keyboard sliding away and update padding to 0
    // The observer will stop itself when visible height reaches 0 or wrapper is removed
  }


  ngAfterViewInit(): void {
    // Roller initialization is already handled in showKeyboard
    // No need to initialize here as it causes double initialization
  }

  // handle view change from cs-segment selection (called by setter)
  private async handleViewChange(view: KeyboardView) {
    // shutdown current state (applies values)
    this.shutdownKeyboard();

    // save the user's preference (don't skip database sync so it persists)
    this.settings.keyboardLastView = view;
    this.settings.saveInBackground('keyboard view');

    // reinitialize with new view
    await this.initializeKeyboard();

    // set selection state when switching to keyboard mode
    if (view === KeyboardView.Keyboard && this.displayValue.length > 0) {
      this.isValueSelected = true;
      // Select the text in the actual input element
      this.selectInputText();
    }

    this.cdr.markForCheck();
  }

  // handle unit change from cs-segment selection (called by setter)
  private async handleUnitChange(unit: string) {
    // direct selection - user clicked on a specific unit
    if (this.unitConfigs.some(c => c.id === unit)) {
      // Apply any pending roller values before switching units
      if (this.currentView === KeyboardView.Roller && this.opts?.onValueSelectedNumeric) {
        void this.returnRollerValues();
      }

      // notify parent component about unit change and wait for it to complete
      if (this.opts.onUnitChange) {
        await this.opts.onUnitChange(unit);
      }

      // now the parent has updated the DOM, reinitialize
      // Don't call shutdownKeyboard() - we want to stay open
      await this.initializeKeyboard();
    }
  }

  // Bound reference for document click listener (for cleanup)
  private boundOnDocumentClick = this.onDocumentClick.bind(this);

  ngOnInit() {
    // If we were created as a popover, initialize immediately
    if (this.targetElement && this.optsProvider) {
      // Show keyboard immediately when created as popover
      this.keyboardShown = true; // Ensure UI is shown for popover mode
      void this.showKeyboard(this.targetElement, this.optsProvider);
    }

    // start monitoring resizing on the bottom sheet wrapper (includes handle and padding)
    this.resizeObserver = new ResizeObserver((entries: ResizeObserverEntry[]) => {
      for (const entry of entries) {
        this.scrollSync.onSized(entry.contentRect);
      }
    });

    // Find the bottom sheet wrapper to observe (it includes the handle and padding)
    const bottomSheetWrapper = this.host.nativeElement.closest('.bottom-sheet-wrapper');
    if (bottomSheetWrapper) {
      this.resizeObserver.observe(bottomSheetWrapper);
    } else {
      // Fallback to observing the host element if wrapper not found
      this.resizeObserver.observe(this.host.nativeElement);
    }

    // Listen for clicks outside the keyboard to close it
    // Use capture phase to catch clicks before they're consumed
    document.addEventListener('click', this.boundOnDocumentClick, true);
  }

  // Handle document clicks to close keyboard when tapping outside
  private onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (!target) return;

    // Ignore if clicking inside the keyboard
    const keyboardWrapper = this.host.nativeElement.closest('.bottom-sheet-wrapper');
    if (keyboardWrapper?.contains(target)) {
      return;
    }

    // Ignore if clicking on a cs-input-measure (keyboard will update to that input)
    if (target.closest('cs-input-measure')) {
      return;
    }

    // Ignore if showing internal popover (like unit selector)
    if (this.isShowingInternalPopover) {
      return;
    }

    // Click was outside keyboard and inputs - close the keyboard
    void this.keyboardService.stopKeyboard();
  }
  ngOnDestroy(): void {
    this.destroyGuard.destroy();
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    // Stop syncing the page to the keyboard
    this.scrollSync.stop();
    // Clean up document click listener
    document.removeEventListener('click', this.boundOnDocumentClick, true);
  }

  onKeyboardClick(ev: Event, keyCode: KeyCode) {
    ev.preventDefault();
    ev.stopPropagation();

    // check for keys that the options disallow
    if (keyCode === KeyCode.NumDot && !this.opts.allowDecimal) {
      return;
    }
    
    // Visual feedback - add pressed class
    const target = ev.target as HTMLElement;
    const keyElement = target.closest('.key') || target.closest('.fraction-key');
    if (keyElement) {
      keyElement.classList.add('pressed');
      setTimeout(() => keyElement.classList.remove('pressed'), 150);
    }
    
    // Sound + haptic feedback
    triggerFeedback(Feedback.KeyboardTap);

    // Handle the key press internally
    this.handleKeyPress(keyCode);
    
    // Update display and scroll to end
    this.cdr.markForCheck();
    this.scrollValueDisplayToEnd();
  }
  
  // Note: onUnitToggle and onViewToggle methods removed - now handled by cs-segment component
  // with two-way binding [(selection)] which triggers the setters above

  async onServingClick(ev: Event) {
    ev.preventDefault();
    await this.presentUnitPopover(ev);
  }

  async onUnitSelectorClick(ev: Event) {
    ev.preventDefault();
    await this.presentUnitPopover(ev);
  }

  private async presentUnitPopover(event: Event) {
    if (event) {
      event.stopPropagation();
    }

    // Mark that we're showing internal popover to prevent keyboard dismissal
    this.isShowingInternalPopover = true;

    try {
      // Get the keyboard element for positioning
      const keyboardElement = this.host.nativeElement as HTMLElement;

      const result = await this.popupMenu.createMenu({
        title: this.opts.isCustomUnitConfig ? LL.common.pickerTitles.servingSize() : LL.common.pickerTitles.selectUnit(),
        buttons: this.unitConfigs.map(c => ({
          text: untranslatedString(c.doNotShortenUnit1 ? c.unit1 : (shortUnitName(c.unit1) || c.unit1)),
          // localized unit description, shown as the help tooltip / label fallback in the picker
          // (df-b0427dd3fe59). translateMeasureDescription resolves c.descriptionKey to the
          // translated string, falling back to the raw English description if a config has no key.
          description: translateMeasureDescription(c),
          value: c.id,
        })),
        value: this.currentUnit,
        targetFromElement: keyboardElement,
      });

      // if user selected a unit
      if (result.value != null) {
        // trigger unit change which will reinitialize keyboard
        await this.handleUnitChange(result.value);
      }

      this.cdr.markForCheck();
    } finally {
      // Always clear the flag when popover is dismissed
      this.isShowingInternalPopover = false;
    }
  }
  
  async onConfirmClick(ev: Event) {
    ev.preventDefault();
    
    // Visual feedback for log button
    const target = ev.target as HTMLElement;
    const buttonElement = target.closest('.log-btn');
    if (buttonElement) {
      buttonElement.classList.add('pressed');
      setTimeout(() => buttonElement.classList.remove('pressed'), 150);
    }

    // Apply the value based on current view
    if (this.currentView === KeyboardView.Roller) {
      // Return selected roller values through unified method
      await this.returnRollerValues();
    } else if (this.currentView === KeyboardView.Keyboard) {
      // Return keyboard value through callback
      await this.returnKeyboardValue();
    }
    
    // If we're focusing the next input, don't blur - just focus the next one
    if (this.nextInSequence.element && this.nextInSequence.action === BorrowedElementAction.Focus) {
      const element = this.nextInSequence.element;
      // For INPUT-MEASURE, find the actual input or display-value element inside
      let inputToFocus = element;
      if (element.tagName === 'CS-INPUT-MEASURE') {
        const innerElement = element.querySelector('input, .display-value') as HTMLElement;
        if (innerElement) {
          inputToFocus = innerElement;
        }
      }
      // For input elements, focus works; for divs (mobile), we need to click
      // since mobile divs don't have tabindex and use click handlers
      if (inputToFocus instanceof HTMLInputElement) {
        inputToFocus.focus();
      } else {
        // Mobile div - click to trigger onDisplayTap
        inputToFocus.click();
      }

      // Wait for Angular to process the focus event
      if (!await this.destroyGuard.safeWaitForAngularUpdate(this.injector)) return;

      // Wait for the focus event handler to complete and update keyboard target
      // The focus event triggers cs-input-measure's onFocusIn which calls startKeyboard
      // which updates this.targetElement and this.optsProvider
      const maxWaitMs = 200;
      const startTime = Date.now();
      while (this.targetElement !== inputToFocus && (Date.now() - startTime) < maxWaitMs) {
        await sleep(10);
      }

      // Give showKeyboard's initializeKeyboard() call time to complete
      await sleep(20);

      // Explicitly reinitialize to ensure this.opts is up to date with the new component
      // By this point, showKeyboard should have updated this.optsProvider, so this will
      // use the correct component's callback
      await this.initializeKeyboard();

      const scrollContainer = findScrollContainer(inputToFocus as HTMLElement);
      if (scrollContainer) {
        const inputRect = (inputToFocus as HTMLElement).getBoundingClientRect();
        const containerRect = scrollContainer.getBoundingClientRect();
        const keyboardHeight = this.keyboardService.keyboardHeight || this.host.nativeElement?.getBoundingClientRect().height || 0;
        const viewportHeight = window.innerHeight;
        
        // If input is below the area that will be visible after keyboard appears
        if (inputRect.bottom > viewportHeight - keyboardHeight - 20) {
          // Calculate how much to scroll
          const scrollAmount = inputRect.bottom - (viewportHeight - keyboardHeight - 40);
          scrollContainer.scrollBy({
            top: scrollAmount,
            behavior: 'smooth'
          });
        } else if (inputRect.top < containerRect.top + 20) {
          // If input is above the visible area, scroll it down
          const scrollAmount = inputRect.top - containerRect.top - 20;
          scrollContainer.scrollBy({
            top: scrollAmount,
            behavior: 'smooth'
          });
        }
      } else {
        // Fallback to standard scrollIntoView
        await scrollIntoViewAsync(inputToFocus as HTMLElement, { behavior: 'smooth', block: 'center' });
      }
      
      return; // Don't close keyboard
    }

    // If we borrowed a button's label, restore it first then click it (if not disabled)
    if (this.nextInSequence.element && this.nextInSequence.action === BorrowedElementAction.Click) {
      // Restore visibility BEFORE clicking
      this.restoreBorrowedElementIfHidden();

      // Only click if not disabled
      if (!this.nextInSequence.isDisabled) {
        // Store the current input element before clicking (targetElement might get cleared)
        const currentInputElement = this.targetElement?.closest('[input-sequence]') as HTMLElement || this.targetElement;
        // Also capture the target ID from the button's input-sequence attribute before clicking
        const targetId = this.nextInSequence.element.getAttribute('input-sequence');
        
        this.nextInSequence.element.click();
        
        // Wait for Angular to update after clicking the button
        // This allows any new elements to be rendered
        if (!await this.destroyGuard.safeWaitForAngularUpdate(this.injector)) return;
        
        // Wait a bit more for DOM updates after the click
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Re-check for a new next element in sequence
        // The button click may have revealed a new input field (and this button may be gone, or may still be here - handle both cases)
        // Use the stored reference since targetElement might be cleared
        let sequenceElement = currentInputElement;
        
        // If current element is part of a paired set, we need to skip past its pair
        const currentPairRole = sequenceElement?.getAttribute('data-pair-role') || sequenceElement?.getAttribute('pair-role');
        const currentPairedWith = sequenceElement?.getAttribute('paired-with');
        if (currentPairRole && currentPairedWith) {
          // Find the paired element
          const pairedId = currentPairedWith.startsWith('#') ? currentPairedWith.substring(1) : currentPairedWith;
          const pairedElement = document.getElementById(pairedId);
          if (pairedElement) {
            // Start search from the paired element if it comes after current element
            const currentRect = sequenceElement.getBoundingClientRect();
            const pairedRect = pairedElement.getBoundingClientRect();
            if (pairedRect.top > currentRect.top || (pairedRect.top === currentRect.top && pairedRect.left > currentRect.left)) {
              sequenceElement = pairedElement as HTMLElement;
            }
          }
        }
        
        let newNextElement: HTMLElement | undefined = undefined;
        
        // First, try to use the target ID if we have one (and it's not empty)
        if (targetId && targetId.trim() !== '') {
          const targetElement = document.getElementById(targetId);
          if (targetElement && targetElement.offsetParent !== null) {
            // Check if it's an input element
            if (targetElement.tagName === 'CS-INPUT-MEASURE' ||
                targetElement.tagName === 'INPUT' ||
                targetElement.tagName === 'CS-INPUT' ||
                targetElement.tagName === 'TEXTAREA') {
              newNextElement = targetElement as HTMLElement;
            }
          }
        }
        
        // If we didn't find via ID, try sequential search
        if (!newNextElement && sequenceElement) {
          newNextElement = findNextVisibleSequenceElement(sequenceElement);
        }

        // No fallback - if no next element is found in the sequence, keyboard should close
        // This prevents the keyboard from jumping to unrelated inputs when completing a set

        // If a new input/input-measure appeared, focus it instead of closing
        if (newNextElement && 
          (newNextElement.tagName === 'CS-INPUT-MEASURE' ||
             newNextElement.tagName === 'INPUT' ||
             newNextElement.tagName === 'CS-INPUT' ||
             newNextElement.tagName === 'TEXTAREA')) {
            
            // Focus the new input or display-value element
            let inputToFocus = newNextElement;
          if (newNextElement.tagName === 'CS-INPUT-MEASURE') {
              const innerElement = newNextElement.querySelector('input, .display-value') as HTMLElement;
              if (innerElement) {
                inputToFocus = innerElement;
              }
            }
            (inputToFocus as HTMLElement).focus();
            
            // Don't close the keyboard - it will update itself for the new input
            return;
        }
      }
    }
    
    // For all other cases, blur the current input to submit its value
    if (this.targetElement) {
      this.targetElement.blur();
    }

    // Close the keyboard if no new input was found
    void this.keyboardService.stopKeyboard();
  }
  
  // Single exit point for returning roller values to the caller
  private async returnRollerValues() {
    // This commit supersedes any scheduled debounced commit - without this,
    // a direct commit (confirm/dismiss/unit change) would be followed by a
    // second, possibly stale commit when the settle timer fires
    this.cancelPendingRollerCommit();

    // Only use numeric callback
    if (!this.opts.onValueSelectedNumeric) return;
    
    // Get numeric values from InputValue objects
    const numeric1 = this.lane1.selected?.numeric || 0;
    const numeric2 = this.isDualRoller ? (this.lane2.selected?.numeric || 0) : undefined;
    
    if (this.isDualRoller) {
      this.opts.onValueSelectedNumeric({ 
        value1: numeric1, 
        value2: numeric2 
      });
    } else if (isFinite(numeric1)) {
      this.opts.onValueSelectedNumeric({ 
        value1: numeric1 
      });
    }
    
    // Wait for Angular to update after the emit
    if (!await this.destroyGuard.safeWaitForAngularUpdate(this.injector)) return;

    // Update next element in case the state changed
    this.updateNextInSequence();
    this.cdr.markForCheck();
  }
  
  onKeyboardBackgroundTap(ev: Event) {
    // Check if the tap is on the roller-list or its children
    const target = ev.target as HTMLElement;
    const isRollerList = target.closest('.roller-list');
    const isRollerItem = target.closest('.roller-item');

    // When used as a popover, let the popover-host handle backdrop clicks
    const isPopover = this.host.nativeElement.closest('.popover-bottom-sheet');

    // Only prevent propagation if not a popover and not interacting with the roller
    if (!isPopover && !isRollerList && !isRollerItem) {
      // Prevent the tap from propagating and causing input to lose focus
      ev.preventDefault();
      ev.stopPropagation();
    }
  }
  
  onHandleTap(ev: Event) {
    ev.preventDefault();
    ev.stopPropagation();
    // Close the keyboard when handle is tapped
    void this.keyboardService.stopKeyboard();
  }

  updateDisplayValue() {
    if (this.targetElement) {
      // For input elements (desktop), use .value; for div elements (mobile), value is already in displayValue
      if (this.targetElement instanceof HTMLInputElement) {
        this.displayValue = this.targetElement.value || '';
      }
      this.cdr.markForCheck();
      this.scrollValueDisplayToEnd();
    }
  }

  async updateDisplayValueAsync() {
    if (this.targetElement) {
      // For input elements (desktop), use .value; for div elements (mobile), value is already in displayValue
      if (this.targetElement instanceof HTMLInputElement) {
        this.displayValue = this.targetElement.value || '';
      }
      if (!await this.destroyGuard.safeWaitForAngularUpdate(this.injector)) return;
      this.scrollValueDisplayToEnd();
    }
  }

  private scrollValueDisplayToEnd() {
    // Auto-scroll to the right to keep cursor visible
    if (this.valueDisplayRef && this.valueDisplayRef.nativeElement) {
      const element = this.valueDisplayRef.nativeElement as HTMLElement;
      element.scrollLeft = element.scrollWidth;
    }
  }

  private selectInputText() {
    // Select all text in the actual HTML input element (desktop only)
    // TODO: FAKE_SELECTION - reimplement for cursor/selection visuals on mobile
    if (this.targetElement instanceof HTMLInputElement) {
      this.targetElement.select();
    }
  }

  private handleKeyPress(keyCode: KeyCode) {
    const decimalSeparator = this.opts.decimalSeparator || '.';

    if (keyCode >= KeyCode.Num0 && keyCode <= KeyCode.Num9) {
      // Add digit
      const digit = keyCode - KeyCode.Num0;

      // If value is selected, replace entire value with new digit
      if (this.isValueSelected) {
        this.displayValue = digit.toString();
        this.isValueSelected = false; // Clear selection after interaction
      } else {
        this.displayValue += digit.toString();
      }
    } else if (keyCode === KeyCode.NumDot) {
      // Add decimal separator if not already present
      if (this.isValueSelected) {
        // Replace with just decimal point when selected
        this.displayValue = '0' + decimalSeparator;
        this.isValueSelected = false;
      } else if (!this.displayValue.includes(decimalSeparator)) {
        this.displayValue += decimalSeparator;
      }
    } else if (keyCode === KeyCode.Backspace) {
      // Remove last character (normal behavior even when selected)
      if (this.displayValue.length >= 1) {
        this.displayValue = this.displayValue.slice(0, -1);
      }
      this.isValueSelected = false; // Clear selection after interaction
    } else if (keyCode === KeyCode.Space) {
      // Add space for fractions
      if (this.opts.allowFractions && !this.displayValue.includes(' ')) {
        if (this.isValueSelected) {
          this.displayValue = ' ';
        } else {
          this.displayValue += ' ';
        }
      }
      this.isValueSelected = false; // Clear selection after interaction
    } else if (keyCode === KeyCode.Slash) {
      // Add slash for fractions
      if (this.opts.allowFractions && !this.displayValue.includes('/')) {
        if (this.isValueSelected) {
          this.displayValue = '/';
        } else {
          this.displayValue += '/';
        }
      }
      this.isValueSelected = false; // Clear selection after interaction
    }
    
    // Send the text value for real-time updates
    // ALWAYS get fresh options to ensure correct callback is used
    const freshOpts = this.getFreshOptions();
    if (freshOpts.onTextChange) {
      this.adoptCorrectedText(freshOpts.onTextChange(this.displayValue));

      // Update next element as the value changes might enable/disable buttons
      // Use safeTimeout to allow Angular to process the change first
      this.destroyGuard.safeTimeout(() => {
        this.updateNextInSequence();
        this.cdr.markForCheck();
      }, 0);
    }
  }
  
  // if the parent gave back a corrected version of the text it was handed,
  // show that instead of what was typed, so the keyboard's preview and the
  // input it is editing never disagree (#4904)
  private adoptCorrectedText(corrected: void | string) {
    if (typeof corrected === 'string' && corrected !== this.displayValue) {
      this.displayValue = corrected;
      this.cdr.markForCheck();
    }
  }

  private async returnKeyboardValue() {
    if (this.opts.onValueSelectedNumeric) {
      // Parse the display value to numeric
      let valueStr = this.displayValue;

      // Call the callback with the numeric value - use fresh options
      const freshOpts = this.getFreshOptions();
      if (freshOpts.onTextChange) {
        this.adoptCorrectedText(freshOpts.onTextChange(valueStr));
      }

      // Wait for Angular to update after the emit
      if (!await this.destroyGuard.safeWaitForAngularUpdate(this.injector)) return;

      // Update next element in case the state changed
      this.updateNextInSequence();
    }
  }
  
  private async setupUnitToggle() {
    // Check if we should always show the unit (even when there's only one)
    // The alwaysShowUnit flag can come from either the unit config or the keyboard options
    this.alwaysShowUnit = (this.unitConfigs.length === 1 && this.unitConfigs[0].alwaysShowUnit === true) || (this.opts as any).alwaysShowUnit === true;

    // Determine unit display mode based on number of units
    if (this.unitConfigs.length >= 2) {
      this.unitDisplayMode = this.unitConfigs.length === 2 ? UnitDisplayMode.Toggle : UnitDisplayMode.Selector;

      // Update the displayed units based on current selection
      this.updateUnitDisplay();

      // If current unit is not in available units, switch to first available
      if (!this.unitConfigs.some(c => c.id === this.currentUnit) && this.unitConfigs.length > 0) {
        this.currentUnit = this.unitConfigs[0].id;
        // Notify parent component about the unit change
        if (this.opts.onUnitChange) {
          if (!await this.destroyGuard.safeWaitForAngularUpdate(this.injector)) return;
          // Double-check that the unit hasn't changed again
          if (this.currentUnit === this.unitConfigs[0].id && this.opts.onUnitChange) {
            await this.opts.onUnitChange(this.currentUnit);
            // Update display value after unit change
            this.updateDisplayValue();
          }
        }
        // Update display after changing current unit
        this.updateUnitDisplay();
      }
    } else if (this.unitConfigs.length === 1) {
      // Only one unit available, check if we should show it anyway
      this.unitDisplayMode = UnitDisplayMode.None;
      this.currentUnit = this.unitConfigs[0].id;
      // If alwaysShowUnit is true, we'll show the static unit display
      if (this.alwaysShowUnit) {
        // Set unit1 for the static display
        this.unit1 = {
          id: this.unitConfigs[0].id,
          label: this.getUnitLabel(this.unitConfigs[0])
        };
        this.unit2 = null;
        this.updateUnitDisplay();
      }
    } else {
      this.unitDisplayMode = UnitDisplayMode.None;
      this.alwaysShowUnit = false;
    }
  }
  
  private updateUnitDisplay() {
    if (this.unitDisplayMode === UnitDisplayMode.Toggle) {
      // For exactly 2 units, show both as toggles
      this.unit1 = {
        id: this.unitConfigs[0].id,
        label: this.getUnitLabel(this.unitConfigs[0])
      };
      this.unit2 = {
        id: this.unitConfigs[1].id,
        label: this.getUnitLabel(this.unitConfigs[1])
      };
    } else if (this.unitDisplayMode === UnitDisplayMode.Selector) {
      // For 3 or more units, show only current unit with selector button
      const currentConfig = this.unitConfigs.find(c => c.id === this.currentUnit);
      this.unit1 = {
        id: this.currentUnit,
        label: currentConfig ? this.getUnitLabel(currentConfig) : this.currentUnit.toUpperCase()
      };
      this.unit2 = null;
    } else if (this.unitDisplayMode === UnitDisplayMode.None && this.alwaysShowUnit && this.unitConfigs.length === 1) {
      // For single unit with alwaysShowUnit, set unit1 for static display
      this.unit1 = {
        id: this.unitConfigs[0].id,
        label: this.getUnitLabel(this.unitConfigs[0])
      };
      this.unit2 = null;
    }
  }
  
  private getUnitLabel(config: MeasureUnitConfig): string {
    // Use shortUnitName to match what's shown in the popover options (unless the config says
    // unit1 must render whole -- see MeasureUnitConfig.doNotShortenUnit1)
    if (config.doNotShortenUnit1) {
      return config.unit1;
    }
    return shortUnitName(config.unit1) || config.unit1;
  }
  
  // This method is no longer needed - use InputValue.findClosest instead
  
  
  // Helper method to update display value and scroll
  private updateDisplayValueAndScroll(value: string) {
    this.displayValue = value;
    this.scrollValueDisplayToEnd();
  }
  
  
  // Helper method for roller feedback
  private playRollerFeedback() {
    triggerFeedback(Feedback.PickerTick);
  }

  // Pending debounced commit of the roller value (see ROLLER_COMMIT_SETTLE_MS)
  private pendingRollerCommitTimer: ReturnType<typeof setTimeout> | null = null;

  // Schedule the roller-value commit for after the wheel settles. Each tick
  // pushes the commit further out, so a moving wheel never commits; the
  // commit runs once, ROLLER_COMMIT_SETTLE_MS after the last tick.
  private scheduleRollerCommit(): void {
    this.cancelPendingRollerCommit();
    this.pendingRollerCommitTimer = this.destroyGuard.safeTimeout(() => {
      this.pendingRollerCommitTimer = null;
      void this.returnRollerValues();
    }, ROLLER_COMMIT_SETTLE_MS);
  }

  // Cancel a scheduled roller-value commit. Called when the value is
  // committed through a direct path instead (confirm/dismiss/unit change),
  // and when the keyboard re-initializes — a commit scheduled for the old
  // target must never fire at the new one.
  private cancelPendingRollerCommit(): void {
    if (this.pendingRollerCommitTimer !== null) {
      this.destroyGuard.cancelTimeout(this.pendingRollerCommitTimer);
      this.pendingRollerCommitTimer = null;
    }
  }

  // Push the current roller values to the parent as a lightweight preview so
  // the input's visible text tracks the wheel live while it is still moving.
  // Runs on every tick, so it must stay cheap: the preview callback only
  // updates the input's text — editDone and everything hanging off it (the
  // plate-loading solver, page-level updates) run in returnRollerValues once
  // the wheel settles. Mirrors returnRollerValues' value extraction.
  private previewRollerValues(): void {
    if (!this.opts.onValuePreviewNumeric) return;

    const numeric1 = this.lane1.selected?.numeric || 0;
    if (this.isDualRoller) {
      this.opts.onValuePreviewNumeric({
        value1: numeric1,
        value2: this.lane2.selected?.numeric || 0,
      });
    } else if (isFinite(numeric1)) {
      this.opts.onValuePreviewNumeric({ value1: numeric1 });
    }
  }

  // The text shown above a dual wheel: each value followed by its own unit,
  // e.g. "5 ft 11 in". Only meaningful when both columns hold a value.
  private dualDisplayText(): string {
    const first = `${this.lane1.selected?.label || ''} ${this.lane1.label?.toLowerCase() || ''}`;
    const second = `${this.lane2.selected?.label || ''} ${this.lane2.label?.toLowerCase() || ''}`;
    return `${first} ${second}`.trim();
  }

  // Move one wheel to a row. Does nothing when the wheel is already there,
  // which is what stops a write from our own code re-running the tick handler.
  // The template calls this from (currentIndexChange); code that positions a
  // wheel calls it directly.
  setLaneIndex(lane: RollerLane, index: number): void {
    if (lane.currentIndex === index) return;
    lane.currentIndex = index;
    this.onLaneIndexChanged(lane, index);
  }

  // Handle roller index changes. Runs on EVERY value the wheel crosses while
  // scrolling, so it must stay cheap: update the keyboard's own display and
  // play the tick, then leave the expensive commit to the settle timer.
  private onLaneIndexChanged(lane: RollerLane, index: number): void {
    // if we have moved to a new value, tick - but not on the very first one,
    // which is the wheel being positioned rather than the user spinning it
    if (index !== lane.lastIndex) {
      const isInitial = lane.lastIndex === -1;
      lane.lastIndex = index;
      if (!isInitial) this.playRollerFeedback();
    }

    lane.selected = lane.values[index];

    this.updateRollerDisplay(lane);

    // Preview the value into the input's text right away, but commit only
    // after the wheel settles - committing per tick ran change detection
    // and the plate solver for every value crossed (#4042)
    this.previewRollerValues();
    this.scheduleRollerCommit();

    this.cdr.markForCheck();
  }

  // Put the wheel's current value into the keyboard's own display, after the
  // named column moved.
  private updateRollerDisplay(lane: RollerLane): void {
    if (this.isDualRoller) {
      // Dual roller mode - update display with both values
      if (this.lane1.selected && this.lane2.selected) {
        this.updateDisplayValueAndScroll(this.dualDisplayText());
      }
      return;
    }

    // Single roller mode - only the first column is on screen, so a move of
    // the unused second column changes nothing the user can see
    if (lane === this.lane1 && lane.selected) {
      this.updateDisplayValueAndScroll(lane.selected.label);
    }
  }

}

